import * as queries from "../graphql/queries";
import { get } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { gql, fetchAllPagesWithGql } from "./gql";
import { sortDataBySortingNumber } from "./helpers";

const adminApiName = "AdminQueries";

const publicAssessmentSubmissionFields = `
  id
  assessmentID
  customerName
  customerMail
  customerPhone
  customerOrganization
  consentMarketingMails
  preferences
  answerData
  evaluationData
  ownerGroups
  createdAt
  updatedAt
  __typename
`;

const createPublicAssessmentSubmissionMutation = /* GraphQL */ `
  mutation CreatePublicAssessmentSubmission(
    $input: CreatePublicAssessmentSubmissionInput!
    $condition: ModelPublicAssessmentSubmissionConditionInput
  ) {
    createPublicAssessmentSubmission(input: $input, condition: $condition) {
      id
    }
  }
`;

const deletePublicAssessmentSubmissionMutation = /* GraphQL */ `
  mutation DeletePublicAssessmentSubmission(
    $input: DeletePublicAssessmentSubmissionInput!
    $condition: ModelPublicAssessmentSubmissionConditionInput
  ) {
    deletePublicAssessmentSubmission(input: $input, condition: $condition) {
      ${publicAssessmentSubmissionFields}
    }
  }
`;

const publicAssessmentSubmissionsByAssessmentQuery = /* GraphQL */ `
  query PublicAssessmentSubmissionsByAssessmentID(
    $assessmentID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelPublicAssessmentSubmissionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    publicAssessmentSubmissionsByAssessmentID(
      assessmentID: $assessmentID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ${publicAssessmentSubmissionFields}
      }
      nextToken
      __typename
    }
  }
`;

const evaluationModuleDataByRelatedEvaluationIdQuery = /* GraphQL */ `
  query EvaluationModuleDataByRelatedEvaluationId(
    $relatedEvaluationId: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelEvaluationModuleDataFilterInput
    $limit: Int
    $nextToken: String
  ) {
    evaluationModuleDataByRelatedEvaluationId(
      relatedEvaluationId: $relatedEvaluationId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        relatedEvaluationId
        moduleID
        resultText
        rating
        maxRating
        questionRating {
          questionID
          rating
          maxRating
          hasAnswer
        }
        completelyAnswered
        ownerGroups
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

function parseAwsJson(value) {
  if (!value) return value;
  if (typeof value !== "string") return value;

  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "string" ? parseAwsJson(parsed) : parsed;
  } catch {
    return value;
  }
}

export async function getUserByUserId(userId) {
  const path = "/getUser";

  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();

  const response = await get({
    apiName: adminApiName,
    path,
    options: {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      queryParams: {
        username: userId,
      },
    },
  }).response;

  return await response.body.json();
}

export async function fetchCustomersAll() {
  return await fetchAllPagesWithGql({
    queryDoc: queries.listCustomers,
    fieldName: "listCustomers",
    variablesBase: {},
    pageSize: 10000,
  });
}

export async function fetchCustomerById(customerId) {
  const data = await gql({
    query: queries.getCustomer,
    variables: { id: customerId },
  });

  const result = data.getCustomer;
  return result ? result : null;
}

export async function fetchCustomersByOwnerGroups(groups) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listCustomers,
    fieldName: "listCustomers",
    variablesBase: {},
    pageSize: 1000,
  });

  const customerArray = [];
  for (const customer of items || []) {
    const ownerGroups = customer.ownerGroups || [];
    if (ownerGroups.some((group) => groups.includes(group))) {
      customerArray.push(customer);
    }
  }

  return customerArray;
}

export async function fetchAllAssessments() {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listAssessments,
    fieldName: "listAssessments",
    variablesBase: {},
    pageSize: 10000,
  });

  return items || [];
}

export async function fetchAllActiveAssessments() {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listAssessments,
    fieldName: "listAssessments",
    variablesBase: { filter: { active: { eq: true } } },
    pageSize: 10000,
  });

  return items || [];
}

export async function fetchAllAssessmentAssignmentsByCustomer(customerId) {
  return await fetchAllPagesWithGql({
    queryDoc: queries.assessmentAssignmentsByCustomerID,
    fieldName: "assessmentAssignmentsByCustomerID",
    variablesBase: { customerID: customerId },
    pageSize: 1000,
  });
}

export async function fetchAssessmentAssignmentById(assessmentAssignmentId) {
  const data = await gql({
    query: queries.getAssessmentAssignment,
    variables: { id: assessmentAssignmentId },
  });

  const result = data.getAssessmentAssignment;
  return result ? result : null;
}

export async function fetchAssessmentAssignmentByAssessment(assessmentId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.assessmentAssignmentsByAssessmentID,
    fieldName: "assessmentAssignmentsByAssessmentID",
    variablesBase: { assessmentID: assessmentId },
    pageSize: 1000,
  });

  return items || [];
}

export async function fetchAssessmentById(assessmentId) {
  const data = await gql({
    query: queries.getAssessment,
    variables: { id: assessmentId },
  });

  const assessment = data.getAssessment;
  return assessment ? assessment : null;
}

export async function fetchModuleById(moduleId) {
  const data = await gql({
    query: queries.getModule,
    variables: { id: moduleId },
  });

  const module = data.getModule;
  return module ? module : null;
}

export async function fetchModulesByAssessment(assessmentId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.modulesByAssessmentID,
    fieldName: "modulesByAssessmentID",
    variablesBase: { assessmentID: assessmentId },
    pageSize: 1000,
  });

  return items || [];
}

export async function fetchQuestionsByModule(moduleId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.questionsByModuleID,
    fieldName: "questionsByModuleID",
    variablesBase: { moduleID: moduleId },
    pageSize: 1000,
  });

  return items || [];
}

export async function fetchQuestionsByModuleFilteredByPackage(
  moduleId,
  filteredPackage,
) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.questionsByModuleID,
    fieldName: "questionsByModuleID",
    variablesBase: {
      moduleID: moduleId,
      filter: { package: { eq: filteredPackage } },
    },
    pageSize: 1000,
  });

  return items || [];
}

export async function fetchQuestionById(questionId) {
  const data = await gql({
    query: queries.getQuestion,
    variables: { id: questionId },
  });
  return data.getQuestion;
}

export async function fetchRequirementsByQuestion(questionId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.requirementsByQuestionID,
    fieldName: "requirementsByQuestionID",
    variablesBase: { questionID: questionId },
    pageSize: 1000,
  });

  return items || [];
}

export async function fetchRecommendationsByRequirement(requirementId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.recommendationsByRequirementID,
    fieldName: "recommendationsByRequirementID",
    variablesBase: { requirementID: requirementId },
    pageSize: 1000,
  });

  return {
    items: (items || []).sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
  };
}

export async function fetchStructuredEntriesByModule(moduleId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.structuredEntriesByModuleID,
    fieldName: "structuredEntriesByModuleID",
    variablesBase: { moduleID: moduleId },
    pageSize: 1000,
  });

  const fullEntries = await Promise.all(
    (items || []).map(async (entry) => fetchStructuredEntryById(entry.id)),
  );

  return fullEntries.filter(Boolean);
}

export async function fetchStructuredEntryById(structuredEntryId) {
  const data = await gql({
    query: queries.getStructuredEntry,
    variables: { id: structuredEntryId },
  });
  return data.getStructuredEntry;
}

export async function fetchPerformedAssessmentByAssessmentAssignment(
  assessmentAssignmentId,
) {
  const data = await gql({
    query: queries.performAssessmentsByAssessmentAssignmentID,
    variables: { assessmentAssignmentID: assessmentAssignmentId },
  });

  const result = data.performAssessmentsByAssessmentAssignmentID;
  return result ? result : null;
}

export async function fetchRequirementAnswerByRequirement(requirementId) {
  const data = await gql({
    query: queries.requirementAnswersByRequirementID,
    variables: { requirementID: requirementId },
  });

  const result = data.requirementAnswersByRequirementID;
  return result ? result : null;
}

export async function fetchRequirementAnswerByPerformAssessment(
  performAssessmentId,
) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.requirementAnswersByPerformedAssessmentID,
    fieldName: "requirementAnswersByPerformedAssessmentID",
    variablesBase: { performedAssessmentID: performAssessmentId },
    pageSize: 1000,
  });

  return { items: items || [] };
}

export async function fetchEvaluationById(evaluationId) {
  const data = await gql({
    query: queries.getEvaluation,
    variables: { id: evaluationId },
  });
  return data.getEvaluation;
}

export async function fetchEvaluationsByAssessmentAssignment(
  assessmentAssignemtId,
) {
  const data = await gql({
    query: queries.evaluationsByAssessmentAssignmentID,
    variables: { assessmentAssignmentID: assessmentAssignemtId },
  });

  const result = data.evaluationsByAssessmentAssignmentID;
  return result ? result : null;
}

function enrichEvaluationWithAssignment(evaluation, assignment, extra = {}) {
  return {
    ...evaluation,
    ...extra,
    state: evaluation.state,
    status: evaluation.state,
    assessmentAssignmentID:
      evaluation.assessmentAssignmentID || assignment?.id || "",
    assessmentID: assignment?.assessmentID || "",
    customerID: assignment?.customerID || "",
    relatedCustomerId: assignment?.customerID || "",
  };
}

export async function fetchAssessmentEvaluations(assessmentId) {
  const assignments = await fetchAssessmentAssignmentByAssessment(assessmentId);
  const nestedEvaluations = await Promise.all(
    (assignments || []).map(async (assignment) => {
      const result = await fetchEvaluationsByAssessmentAssignment(
        assignment.id,
      );

      return (result?.items || []).map((evaluation) =>
        enrichEvaluationWithAssignment(evaluation, assignment),
      );
    }),
  );

  return nestedEvaluations
    .flat()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export async function fetchAllEvaluations() {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listEvaluations,
    fieldName: "listEvaluations",
    variablesBase: {
      filter: {
        assessmentAssignmentID: { attributeExists: true },
        performedAssessmentID: { attributeExists: true },
      },
    },
    pageSize: 1000,
  });

  return (items || [])
    .filter(Boolean)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export async function fetchEvaluationModuleDataByEvaluation(evaluationId) {
  const data = await gql({
    query: evaluationModuleDataByRelatedEvaluationIdQuery,
    variables: { relatedEvaluationId: evaluationId },
  });

  const result = data.evaluationModuleDataByRelatedEvaluationId;
  return result ? result.items : null;
}

//Module (Assessments)
export async function fetchAllModules() {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listModules,
    fieldName: "listModules",
    variablesBase: {},
    pageSize: 10000,
  });

  return items || [];
}

export async function fetchAllCustomers(onlyActive) {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listCustomers,
    fieldName: "listCustomers",
    variablesBase: { ...(onlyActive && { filter: { active: { eq: true } } }) },
    pageSize: 10000,
  });

  return items || [];
}

// Daten für die Module einer Evaluation

export async function fetchDataForEvaluation(evaluationId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: evaluationModuleDataByRelatedEvaluationIdQuery,
    fieldName: "evaluationModuleDataByRelatedEvaluationId",
    variablesBase: { relatedEvaluationId: evaluationId },
    pageSize: 10000,
  });

  return (items || [])
    .map((data) => {
      const content = parseAwsJson(data.content);
      return content && typeof content === "object"
        ? { ...data, ...content }
        : data;
    })
    .sort(sortDataBySortingNumber);
}

//Alle Evaluationen eines Mandanten
export async function fetchCustomersEvaluations(customerId) {
  const assignments = await fetchAllAssessmentAssignmentsByCustomer(customerId);
  const nestedEvaluations = await Promise.all(
    (assignments || []).map(async (assignment) => {
      const result = await fetchEvaluationsByAssessmentAssignment(
        assignment.id,
      );

      return (result?.items || []).map((evaluation) =>
        enrichEvaluationWithAssignment(evaluation, assignment),
      );
    }),
  );

  return nestedEvaluations
    .flat()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

//Alle Evaluationen eines Moduls

export async function fetchModulesEvaluations(moduleId) {
  const moduleDataItems = await fetchAllPagesWithGql({
    queryDoc: queries.listEvaluationModuleData,
    fieldName: "listEvaluationModuleData",
    variablesBase: { filter: { moduleID: { eq: moduleId } } },
    pageSize: 1000,
  });

  const moduleDataByEvaluation = new Map();
  for (const item of moduleDataItems || []) {
    if (item.relatedEvaluationId && !moduleDataByEvaluation.has(item.relatedEvaluationId)) {
      moduleDataByEvaluation.set(item.relatedEvaluationId, item);
    }
  }

  const evaluations = await Promise.all(
    Array.from(moduleDataByEvaluation.entries()).map(
      async ([evaluationId, moduleData]) => {
        const evaluation = await fetchEvaluationById(evaluationId);
        if (!evaluation) return null;

        const assignment = evaluation.assessmentAssignmentID
          ? await fetchAssessmentAssignmentById(evaluation.assessmentAssignmentID)
          : null;

        return enrichEvaluationWithAssignment(evaluation, assignment, {
          moduleID: moduleId,
          relatedModuleId: moduleId,
          evaluationModuleDataItem: moduleData,
        });
      },
    ),
  );

  return evaluations
    .filter(Boolean)
    .sort((a, b) =>
      (a.relatedCustomerId || "").localeCompare(b.relatedCustomerId || ""),
    );
}

//Public Assessments
export async function fetchAllPublicAssessments() {
  const items = await fetchAllPagesWithGql({
    queryDoc: queries.listAssessments,
    fieldName: "listAssessments",
    variablesBase: {
      filter: { public: { eq: true } },
    },
    pageSize: 10000,
  });

  return items || [];
}

export async function fetchPublicAssessmentById(
  assessmentId,
  { authMode } = {},
) {
  const data = await gql({
    query: queries.getAssessment,
    variables: { id: assessmentId },
    authMode,
  });

  const assessment = data.getAssessment;
  if (!assessment) return null;

  const assessmentModules = await fetchPublicAssessmentModules(assessmentId, {
    authMode,
  });

  return {
    ...assessment,
    title: assessment.name,
    assessmentModules: JSON.stringify(assessmentModules),
  };
}

export async function fetchPublicAssessmentModules(
  assessmentId,
  { authMode } = {},
) {
  const modules = await fetchAllPagesWithGql({
    queryDoc: queries.modulesByAssessmentID,
    fieldName: "modulesByAssessmentID",
    variablesBase: { assessmentID: assessmentId },
    pageSize: 10000,
    authMode,
  });

  const moduleEntries = await Promise.all(
    (modules || []).sort(sortDataBySortingNumber).map(async (module, index) => {
      const questions = await fetchAllPagesWithGql({
        queryDoc: queries.questionsByModuleID,
        fieldName: "questionsByModuleID",
        variablesBase: { moduleID: module.id },
        pageSize: 10000,
        authMode,
      });

      const questionEntries = await Promise.all(
        (questions || []).map(async (question, questionIndex) => {
          const requirements = await fetchAllPagesWithGql({
            queryDoc: queries.requirementsByQuestionID,
            fieldName: "requirementsByQuestionID",
            variablesBase: { questionID: question.id },
            pageSize: 10000,
            authMode,
          });

          const requirementEntries = await Promise.all(
            (requirements || []).map(async (requirement) => {
              const recommendations = await fetchAllPagesWithGql({
                queryDoc: queries.recommendationsByRequirementID,
                fieldName: "recommendationsByRequirementID",
                variablesBase: { requirementID: requirement.id },
                pageSize: 10000,
                authMode,
              });

              const sortedRecommendations = (recommendations || []).sort(
                (a, b) => (a.level || 0) - (b.level || 0),
              );

              return [
                requirement.id,
                {
                  ...requirement,
                  solution:
                    sortedRecommendations[0]?.recommendationText || "",
                  recommendations: sortedRecommendations,
                },
              ];
            }),
          );

          return [
            question.id,
            {
              ...question,
              index: questionIndex + 1,
              topic: question.title,
              content: question.text,
              requirements: Object.fromEntries(requirementEntries),
            },
          ];
        }),
      );

      return [
        module.id,
        {
          ...module,
          index: module.sortingNumber ?? index + 1,
          questions: Object.fromEntries(questionEntries),
        },
      ];
    }),
  );

  return Object.fromEntries(moduleEntries);
}

//Public Assessment Data
export async function createPublicAssessmentSubmission(input) {
  const data = await gql({
    query: createPublicAssessmentSubmissionMutation,
    variables: { input },
    authMode: "apiKey",
  });

  return data.createPublicAssessmentSubmission;
}

export async function fetchAllPublicAssessmentData(assessmentId) {
  const items = await fetchAllPagesWithGql({
    queryDoc: publicAssessmentSubmissionsByAssessmentQuery,
    fieldName: "publicAssessmentSubmissionsByAssessmentID",
    variablesBase: { assessmentID: assessmentId },
    pageSize: 10000,
  });

  return (items || [])
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((submission) => ({
      ...submission,
      answerData: parseAwsJson(submission.answerData),
      evaluationData: parseAwsJson(submission.evaluationData),
    }));
}

export async function deletePublicAssessmentSubmission(id) {
  const data = await gql({
    query: deletePublicAssessmentSubmissionMutation,
    variables: { input: { id } },
  });

  return data.deletePublicAssessmentSubmission;
}
